import { tokens } from '@/lib/tokens.js';
import { useState } from 'react';
import { useAdminGrants, useDeleteGrant } from '../../hooks/use-admin-grants.js';
import type { GrantListParams } from '../../lib/admin-client.js';
import { LevelBadge } from '../shared/status-badge.js';

const s = {
  container: {
    backgroundColor: tokens.colors.bgCardDark,
    borderRadius: tokens.radius.card,
    overflow: 'hidden',
    boxShadow: '0 4px 6px -1px rgba(0,0,0,.1)',
  },
  header: {
    padding: '1rem 1.5rem',
    borderBottom: `1px solid ${tokens.colors.borderDark}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: { fontSize: '1rem', fontWeight: 600, color: tokens.colors.textDark, margin: 0 },
  filters: {
    padding: '0.75rem 1.5rem',
    borderBottom: `1px solid ${tokens.colors.borderDark}`,
    display: 'flex',
    gap: '0.75rem',
    flexWrap: 'wrap' as const,
  },
  input: {
    backgroundColor: '#0f172a',
    border: `1px solid ${tokens.colors.borderDark}`,
    borderRadius: tokens.radius.input,
    color: tokens.colors.textDark,
    padding: '0.375rem 0.75rem',
    fontSize: '0.875rem',
    outline: 'none',
    width: '180px',
  },
  table: { width: '100%', borderCollapse: 'collapse' as const },
  th: {
    padding: '0.75rem 1rem',
    textAlign: 'left' as const,
    fontSize: '0.75rem',
    fontWeight: 600,
    color: tokens.colors.textMutedDark,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    borderBottom: `1px solid ${tokens.colors.borderDark}`,
    backgroundColor: tokens.colors.bgCardDark,
    position: 'sticky' as const,
    top: 0,
  },
  td: {
    padding: '0.75rem 1rem',
    fontSize: '0.875rem',
    color: tokens.colors.textDark,
    borderBottom: '1px solid rgba(55,65,81,0.5)',
  },
  trOdd: { backgroundColor: 'rgba(17,24,39,0.3)' },
  btn: {
    backgroundColor: tokens.colors.primary,
    color: '#fff',
    border: 'none',
    borderRadius: tokens.radius.button,
    padding: '0.375rem 0.875rem',
    fontSize: '0.875rem',
    cursor: 'pointer',
    fontWeight: 500,
  },
  btnSmDanger: {
    backgroundColor: 'transparent',
    color: tokens.colors.error,
    border: `1px solid ${tokens.colors.error}`,
    borderRadius: tokens.radius.button,
    padding: '0.25rem 0.625rem',
    fontSize: '0.8125rem',
    cursor: 'pointer',
  },
};

interface GrantsListProps {
  tenantId: string;
  onGrantClick?: () => void;
  onMatrixClick?: () => void;
}

export function GrantsList({ tenantId, onGrantClick, onMatrixClick }: GrantsListProps) {
  const [params, setParams] = useState<GrantListParams>({ 'page-number': 1, 'page-size': 20 });
  const { data, isLoading } = useAdminGrants(tenantId, params);
  const deleteGrant = useDeleteGrant(tenantId);

  function handleDelete(grantId: string) {
    if (!window.confirm('Delete this grant?')) return;
    deleteGrant.mutate(grantId);
  }

  const items = data?.items ?? [];
  const pagination = data?.pagination;

  return (
    <div style={s.container}>
      <div style={s.header}>
        <h2 style={s.title}>Grants</h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            type="button"
            style={{
              ...s.btn,
              backgroundColor: 'transparent',
              border: `1px solid ${tokens.colors.borderDark}`,
              color: tokens.colors.textDark,
            }}
            onClick={onMatrixClick}
            aria-label="Grants matrix"
          >
            Matrix
          </button>
          <button type="button" style={s.btn} onClick={onGrantClick} aria-label="Create grant">
            + New Grant
          </button>
        </div>
      </div>
      <div style={s.filters}>
        <input
          style={s.input}
          placeholder="Filter by report ID…"
          onChange={(e) => {
            const v = e.target.value;
            setParams((p) => ({ ...p, ...(v ? { tenant_report_id: v } : {}), 'page-number': 1 }));
          }}
          aria-label="Filter by tenant report ID"
        />
        <select
          style={s.input}
          onChange={(e) => {
            const v = e.target.value as 'role' | 'user' | '';
            setParams((p) => ({ ...p, ...(v ? { subject_type: v } : {}), 'page-number': 1 }));
          }}
          aria-label="Filter by subject type"
        >
          <option value="">All subjects</option>
          <option value="role">Role</option>
          <option value="user">User</option>
        </select>
      </div>
      {isLoading ? (
        <output
          style={{
            display: 'block',
            padding: '2rem',
            textAlign: 'center',
            color: tokens.colors.textMutedDark,
          }}
          aria-label="Loading grants"
        >
          Loading…
        </output>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={s.table} aria-label="Grants list">
            <thead>
              <tr>
                <th style={s.th}>Report ID</th>
                <th style={s.th}>Subject type</th>
                <th style={s.th}>Subject ID</th>
                <th style={s.th}>Level</th>
                <th style={s.th}>Data scope</th>
                <th style={s.th} />
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    style={{ ...s.td, textAlign: 'center', color: tokens.colors.textMutedDark }}
                  >
                    No grants found
                  </td>
                </tr>
              ) : (
                items.map((g, idx) => (
                  <tr key={g.id} style={idx % 2 === 1 ? s.trOdd : undefined}>
                    <td style={{ ...s.td, fontFamily: 'monospace', fontSize: '0.8125rem' }}>
                      {g.tenant_report_id}
                    </td>
                    <td style={s.td}>{g.subject_type}</td>
                    <td style={{ ...s.td, fontFamily: 'monospace', fontSize: '0.8125rem' }}>
                      {g.subject_id}
                    </td>
                    <td style={s.td}>
                      <LevelBadge level={g.level} />
                    </td>
                    <td
                      style={{ ...s.td, color: tokens.colors.textMutedDark, fontSize: '0.8125rem' }}
                    >
                      {(g.data_scope?.length ?? 0) > 0 ? `${g.data_scope?.length} clause(s)` : '—'}
                    </td>
                    <td style={s.td}>
                      <button
                        type="button"
                        style={s.btnSmDanger}
                        onClick={() => handleDelete(g.id)}
                        aria-label="Delete grant"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
      {pagination && (pagination.total_pages ?? 1) > 1 && (
        <div
          style={{
            padding: '0.75rem 1.5rem',
            display: 'flex',
            justifyContent: 'space-between',
            borderTop: `1px solid ${tokens.colors.borderDark}`,
            fontSize: '0.875rem',
            color: tokens.colors.textMutedDark,
          }}
        >
          <span>
            Page {pagination.page_number} of {pagination.total_pages}
          </span>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              type="button"
              style={{ ...s.btn, opacity: (params['page-number'] ?? 1) <= 1 ? 0.5 : 1 }}
              disabled={(params['page-number'] ?? 1) <= 1}
              onClick={() =>
                setParams((p) => ({ ...p, 'page-number': (p['page-number'] ?? 1) - 1 }))
              }
              aria-label="Previous page"
            >
              Prev
            </button>
            <button
              type="button"
              style={{
                ...s.btn,
                opacity: (params['page-number'] ?? 1) >= pagination.total_pages ? 0.5 : 1,
              }}
              disabled={(params['page-number'] ?? 1) >= pagination.total_pages}
              onClick={() =>
                setParams((p) => ({ ...p, 'page-number': (p['page-number'] ?? 1) + 1 }))
              }
              aria-label="Next page"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
